import React from 'react';

import classNames from 'classnames';
import styled from 'styled-components';

const Container = styled.div`
  flex: 1 1 0;
  min-width: 0;
  max-width: 254px;
  box-sizing: border-box;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 7.5px 7px;
  border: 1px solid var(--line-gray);
  border-radius: 10px;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;

  &:hover {
    border-color: var(--line-gray-strong);
  }

  &.selected {
    border-color: var(--button-blue);
    background: var(--light-blue);
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  &.disabled:hover {
    border-color: var(--line-gray);
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  min-width: 0;
  overflow: hidden;
`;

const IconBox = styled.div`
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  flex-grow: 0;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;

  &.mint {
    background-image: linear-gradient(to bottom, var(--mint-pale), var(--mint-soft));
  }
  &.gold {
    background-image: linear-gradient(to bottom, var(--gold-pale), var(--gold-soft));
  }
  &.pink {
    background-image: linear-gradient(to bottom, var(--pink-pale), var(--pink-soft) 100%);
  }

  &.standalone {
    background: none;

    img {
      width: 38px;
      height: 38px;
    }
  }
  &.blue {
    background-image: linear-gradient(to bottom, var(--blue-mist), var(--blue-sky));
  }
`;

const IconImage = styled.img`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`;

const MainBody = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
`;

const Title = styled.span`
  font-size: 14px;
  font-family: var(--font-bold);
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.5px;
`;

const Subtitle = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 12px;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

type Props = {
  iconClassName: 'mint' | 'gold' | 'pink' | 'blue' | 'standalone';
  iconImageSrc: string;
  title: string;
  subtitle: React.ReactNode;
  selected: boolean;
  fitContent?: boolean;
  disabled?: boolean;
  onClick: () => void;
};

const AICISummaryCard = ({
  iconClassName,
  iconImageSrc,
  title,
  subtitle,
  selected,
  fitContent,
  disabled,
  onClick,
}: Props) => (
  <Container
    className={classNames({ selected, disabled, 'fit-content': fitContent })}
    onClick={disabled ? undefined : onClick}
  >
    <MainContent>
      <IconBox className={iconClassName}>
        <IconImage src={iconImageSrc} alt="" />
      </IconBox>
      <MainBody>
        <Title title={title}>{title}</Title>
        <Subtitle>{subtitle}</Subtitle>
      </MainBody>
    </MainContent>
  </Container>
);

export default AICISummaryCard;
